import { Json }			from "ts-base/json";
import { JsonReflect }	from "ts-base/jsonReflect";
import { Objects }		from "ts-base/objects";
import { Validated }	from "ts-base/validated";
import { Nea }			from "ts-base/nea";

import { Validator }	from "ts-base/validation/validator";

/** takes some unvalidated json data and returns validated data - or why validation failed */
export type Schema<T>	= Validator<Input, T>;

/** un-validated input value */
export type Input		= unknown;

/** validated output value, or why validation failed */
export type Output<X>	= Validator.Output<X>;

//-----------------------------------------------------------------------------
//## combinators

/** accepts all input and returns void unconditionally */
export const irrelevant:Schema<void>	= Validator.constant(void(0));

/** only this exact value is considered valid */
export const exactly	= <T extends string|number|boolean|null>(tag:T):Schema<T>	=>
	Validator.exactly(tag);

/** enhance a validator so it additionally accepts null values */
export const nullable	= <T>(base:Schema<T>):Schema<T|null>	=>
	Validator.nullable(base);

/** every element in the given set of values is valid, everything else is not. adds the list of valid values to the error message. */
export const enumValue	= <T extends string>(values:ReadonlyArray<T>):Schema<T>	=>
	Validated.andThen(
		string,
		Validator.enumValue(values)
	);

/** every element in the given set of values is valid, everything else is not. does not add the list of valid values to the error message - useful if there are a lot of them. */
export const enumValueNamed	= <T extends string>(name:string, values:ReadonlyArray<T>):Schema<T>	=>
	Validated.andThen(
		string,
		Validator.enumValueNamed(name, values)
	);

/** enhances a validator to validate a whole array of base values */
export const arrayOf	= <T>(base:Schema<T>):Schema<ReadonlyArray<T>>	=>
	Validated.andThen(
		baseArray,
		Validator.array(base)
	);

/** builds a validator for an object from an object of validators for each field of the output object */
export const objectOf	= <T extends Record<string, unknown>>(spec:Validator.SelfRecord<Input, T>):Schema<Readonly<T>> =>
	Validated.andThen(
		baseObject,
		Validator.fields(spec, maybeField)
	);

/** enhances a validator to validate a whole Nea of base values */
export const neaOf	= <T>(base:Schema<T>):Schema<Nea<T>>	=>
	Validated.andThen3(
		baseArray,
		Validator.nonEmpty(),
		Validator.nea(base)
	);

/*
export const recordOf	= <K extends string, V>(key:Validator<string,K>, value:Schema<V>):Schema<Record<K,V>> =>
	Validated.andThen(
		object,
		Validators.record(stringThen(key), value)
	);
*/

/** allows trying different alternatives - the first valid one wins */
export const oneOf	= <T extends ReadonlyArray<Schema<unknown>>>(...partials:T):Schema<Validator.Output<T[number]>>	=>
	Validator.oneOf(...partials);

//-----------------------------------------------------------------------------
//## json types

export const anything:Schema<Json>		= input => JsonReflect.isJson(input)		? Validated.valid(input)	: Validated.invalid1("expected valid json");
export const string:Schema<string>		= input => JsonReflect.isJsonString(input)	? Validated.valid(input)	: Validated.invalid1("expected a string");
export const number:Schema<number>		= input => JsonReflect.isJsonNumber(input)	? Validated.valid(input)	: Validated.invalid1("expected a number");
export const boolean:Schema<boolean>	= input => JsonReflect.isJsonBoolean(input)	? Validated.valid(input)	: Validated.invalid1("expected a boolean");
export const nullValue:Schema<null>		= input => JsonReflect.isJsonNull(input)	? Validated.valid(input)	: Validated.invalid1("expected a null");
export const array:Schema<Json.Array>	= input => JsonReflect.isJsonArray(input)	? Validated.valid(input)	: Validated.invalid1("expected an array");
export const object:Schema<Json.Object>	= input => JsonReflect.isJsonObject(input)	? Validated.valid(input)	: Validated.invalid1("expected an object");

//-----------------------------------------------------------------------------
//## json combinators

export const stringThen	= <T>(base:Validator<string, T>):Schema<T>	=>
	Validated.andThen(string, base);

export const numberThen	= <T>(base:Validator<number, T>):Schema<T>	=>
	Validated.andThen(number, base);

export const booleanThen	= <T>(base:Validator<boolean, T>):Schema<T>	=>
	Validated.andThen(boolean, base);

export const nullThen	= <T>(base:Validator<null, T>):Schema<T>	=>
	Validated.andThen(nullValue, base);

//-----------------------------------------------------------------------------
//## utilities

export const baseArray:Schema<JsonReflect.BaseArray>	= input => JsonReflect.isBaseArray(input)	? Validated.valid(input)	: Validated.invalid1("expected an array");
export const baseObject:Schema<JsonReflect.BaseObject>	= input => JsonReflect.isBaseObject(input)	? Validated.valid(input)	: Validated.invalid1("expected an object");

export const maybeField	= (key:string):Validator<object, unknown>	=> o =>
	Objects.hasOwn(o, key)
	?	Validated.valid(o[key])
	:	Validated.invalid1("field is missing");
